import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from '../../utils/axiosConfig';
import {
  UserGroupIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  DocumentArrowDownIcon,
} from '@heroicons/react/24/outline';
import { useToast } from '../../hooks/useToast';
import { formatCursoLabel } from '../../utils/cursoLabel';

interface Curso {
  id: number;
  nombre: string;
  codigo: string;
  anio?: number;
  tipoEvaluacion: string;
  cantidadTemas: number;
}

interface AlumnoResumen {
  alumno: {
    id: number;
    nombre: string;
    apellido: string;
    numeroAlumno: string;
    email: string | null;
  };
  fecha1: { fecha: string | null; notas: string[] };
  fecha2: { fecha: string | null; notas: string[] };
  fecha3: { fecha: string | null; notas: string[] };
  notaFinal: string[];
  condicion: 'APROBADO' | 'DESAPROBADO' | 'AUSENTE';
}

interface ResumenNotas {
  curso: Curso;
  fechas: {
    fecha1: { id: number; fecha: string; tipo: string; descripcion: string | null } | null;
    fecha2: { id: number; fecha: string; tipo: string; descripcion: string | null } | null;
    fecha3: { id: number; fecha: string; tipo: string; descripcion: string | null } | null;
  };
  alumnos: AlumnoResumen[];
  totalAlumnos: number;
  aprobados: number;
}

const ColaboradoresPage: React.FC = () => {
  const [selectedCurso, setSelectedCurso] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { showSuccess, showError } = useToast();

  const { data: cursos, isLoading: loadingCursos } = useQuery({
    queryKey: ['cursos'],
    queryFn: async () => {
      const response = await axios.get('/cursos');
      return Array.isArray(response.data) ? response.data : [];
    },
  });

  const { data: resumen, isLoading: loadingResumen } = useQuery<ResumenNotas>({
    queryKey: ['resumen-notas-colaboradores', selectedCurso],
    queryFn: async () => {
      if (!selectedCurso) return null;
      const response = await axios.get(`/notas/curso/${selectedCurso}/resumen-notas`);
      return response.data;
    },
    enabled: !!selectedCurso,
  });

  const aprobados: AlumnoResumen[] = useMemo(() => {
    if (!resumen?.alumnos) return [];
    const lista = resumen.alumnos.filter(a => a.condicion === 'APROBADO');
    if (!searchTerm.trim()) return lista;
    const term = searchTerm.toLowerCase().trim();
    return lista.filter(a =>
      a.alumno.apellido.toLowerCase().includes(term) ||
      a.alumno.nombre.toLowerCase().includes(term) ||
      a.alumno.numeroAlumno.toLowerCase().includes(term) ||
      (a.alumno.email && a.alumno.email.toLowerCase().includes(term))
    );
  }, [resumen?.alumnos, searchTerm]);

  const handleExportExcel = () => {
    if (!resumen || aprobados.length === 0) {
      showError('No hay datos para exportar');
      return;
    }

    try {
      const headers = ['#', 'Legajo', 'Apellido', 'Nombre', 'Email'];
      const rows = aprobados.map((a, index) => [
        (index + 1).toString(),
        a.alumno.numeroAlumno,
        a.alumno.apellido,
        a.alumno.nombre,
        a.alumno.email || '',
      ]);

      const csvContent = [
        `Posibles Colaboradores - ${formatCursoLabel(resumen.curso)}`,
        `Total aprobados: ${resumen.aprobados} de ${resumen.totalAlumnos} inscritos`,
        '',
        headers.join('\t'),
        ...rows.map(row => row.join('\t')),
      ].join('\n');

      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `colaboradores_${resumen.curso.codigo}_${(resumen.curso as any).anio || ''}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showSuccess('Exportación exitosa');
    } catch {
      showError('Error al exportar');
    }
  };

  return (
    <div className="space-y-6">
      {/* Encabezado */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-1 flex items-center gap-3">
          <UserGroupIcon className="h-8 w-8 text-indigo-600" />
          Posibles Colaboradores
        </h1>
        <p className="text-sm text-gray-500 mb-6">
          Alumnos que aprobaron la cursada (condición APROBADO). Seleccioná un curso para ver el listado.
        </p>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Seleccionar Curso</label>
          <select
            value={selectedCurso || ''}
            onChange={(e) => {
              setSelectedCurso(e.target.value ? parseInt(e.target.value) : null);
              setSearchTerm('');
            }}
            className="w-full md:w-80 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            disabled={loadingCursos}
          >
            <option value="">— Elegí un curso —</option>
            {Array.isArray(cursos) && (cursos as Curso[]).map(c => (
              <option key={c.id} value={c.id}>{formatCursoLabel(c)}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Contenido */}
      {selectedCurso && (
        <>
          {loadingResumen ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto" />
                <p className="mt-4 text-gray-600">Cargando datos...</p>
              </div>
            </div>
          ) : resumen ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Barra de herramientas */}
              <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium text-gray-700">
                    {aprobados.length} aprobado{aprobados.length !== 1 ? 's' : ''}
                    {searchTerm && ` (filtrado de ${resumen.aprobados})`}
                    {' '}/ {resumen.totalAlumnos} inscritos
                  </span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                    {resumen.aprobados > 0
                      ? `${((resumen.aprobados / resumen.totalAlumnos) * 100).toFixed(1)}% aprobación`
                      : 'Sin aprobados'}
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  {/* Buscador */}
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Buscar alumno..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 pr-8 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 w-52"
                    />
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  {/* Exportar */}
                  <button
                    onClick={handleExportExcel}
                    disabled={aprobados.length === 0}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-40 transition-colors"
                  >
                    <DocumentArrowDownIcon className="h-4 w-4" />
                    Exportar Excel
                  </button>
                </div>
              </div>

              {/* Tabla */}
              {aprobados.length === 0 ? (
                <div className="px-6 py-12 text-center text-gray-500">
                  {resumen.aprobados === 0
                    ? 'No hay alumnos aprobados en este curso.'
                    : 'Ningún alumno coincide con la búsqueda.'}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                          #
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Legajo
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Apellido
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Nombre
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Condición
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {aprobados.map((a, index) => (
                        <tr key={a.alumno.id} className="hover:bg-indigo-50 transition-colors">
                          <td className="px-4 py-3 text-sm text-gray-500 text-center">
                            {index + 1}
                          </td>
                          <td className="px-4 py-3 text-sm font-mono text-gray-700">
                            {a.alumno.numeroAlumno}
                          </td>
                          <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                            {a.alumno.apellido}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {a.alumno.nombre}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {a.alumno.email ? (
                              <a
                                href={`mailto:${a.alumno.email}`}
                                className="text-indigo-600 hover:text-indigo-800 hover:underline"
                              >
                                {a.alumno.email}
                              </a>
                            ) : (
                              <span className="text-gray-400 italic">sin email</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200">
                              Aprobado
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center text-gray-500">
              No se pudo cargar el resumen de notas para este curso.
            </div>
          )}
        </>
      )}

      {!selectedCurso && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <UserGroupIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Seleccioná un curso para ver los alumnos aprobados.</p>
        </div>
      )}
    </div>
  );
};

export default ColaboradoresPage;
